import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Eyebrow + h1 + description page header. The "small" header used
 * when a Hero would be overkill — typical of admin pages, settings
 * screens, secondary routes. Consistent across all admin pages so the
 * "Admin" eyebrow accent always means the same thing.
 *
 * Eyebrow accent colour follows the tone token by default (accent),
 * which is the indigo for non-admin and stays indigo for admin too
 * (the page-level data-density wrapper is what makes admin denser,
 * not the header accent).
 */
export function PageHeader({
  eyebrowIcon: EyebrowIcon,
  eyebrow,
  title,
  description,
  trailing,
  className = '',
}: {
  eyebrowIcon?: LucideIcon;
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={`flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between ${className}`}
    >
      <div className="min-w-0">
        {eyebrow ? (
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-accent">
            {EyebrowIcon ? <EyebrowIcon className="h-3.5 w-3.5" /> : null}
            <span>{eyebrow}</span>
          </div>
        ) : null}
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-fg sm:text-3xl">
          {title}
        </h1>
        {description ? (
          <p className="mt-1 text-sm text-fg-muted">{description}</p>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </header>
  );
}
